import React, { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Button, CircularProgress, Link, TextField } from '@material-ui/core'
import IconButton from '@material-ui/core/IconButton'
import InputAdornment from '@material-ui/core/InputAdornment'
import { makeStyles } from '@material-ui/core/styles'
import Visibility from '@material-ui/icons/Visibility'
import VisibilityOff from '@material-ui/icons/VisibilityOff'
import { allPropsTrue } from 'helpers'
import { getServerError, clearServerErrors } from 'utils/form'
import useTranslation from 'Hooks/useTranslation'

const useStyles = makeStyles(() => ({
    visibilityIcon: {
        fontSize: 12,
    },
    eyeButton: {
        padding: 6,
    },
}))

const SigninLink = React.forwardRef((props, ref) => <RouterLink innerRef={ref}
    to="/signin" {...props} />)

const SignUpForm = props => {

    const {
        request,
        handleSubmit,
        register,
        onSubmit,
        watch
    } = props

    const classes = useStyles()
    const [errors, setErrors] = useState(null)
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const translation = useTranslation();

    const canSubmit = useMemo(() => allPropsTrue(watch()), [watch])

    useEffect(() => {
        if (!request.error) return
        if (request.error.errors && Object.keys(request.error.errors).length) {
            setErrors(request.error.errors)
        } else {
            setErrors(null)
        }
    }, [request.error, setErrors])

    const eyeAdornment = (show, toggle) => (
        <InputAdornment position="end">
            <IconButton
                aria-label={show ? 'Hide password' : 'Show password'}
                className={classes.eyeButton}
                onClick={toggle}
            >
                {show
                    ? <VisibilityOff className={classes.visibilityIcon} />
                    : <Visibility className={classes.visibilityIcon} />
                }
            </IconButton>
        </InputAdornment>
    )

    return (
        <form onSubmit={handleSubmit(onSubmit)} noValidate>

            <TextField
                inputRef={register}
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="licenseId"
                label={translation.signUpPageLicenseIdLabel}
                name="licenseId"
                onFocus={() => setErrors(errors => clearServerErrors(errors, 'licenceId'))}
                error={!!getServerError(errors, 'licenceId')}
                helperText={getServerError(errors, 'licenceId')}
            />
            <TextField
                inputRef={register}
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="email"
                label={translation.signUpPageEmailLabel}
                type="email"
                name="email"
                autoComplete="email"
                onFocus={() => setErrors(errors => clearServerErrors(errors, 'email'))}
                error={!!getServerError(errors, 'email')}
                helperText={getServerError(errors, 'email')}
            />
            <TextField
                inputRef={register}
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label={translation.signUpPagePasswordLabel}
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                onFocus={() => setErrors(errors => clearServerErrors(errors, 'password'))}
                error={!!getServerError(errors, 'password')}
                helperText={getServerError(errors, 'password')}
                InputProps={{ endAdornment: eyeAdornment(showPassword, () => setShowPassword(v => !v)) }}
            />
            <TextField
                inputRef={register}
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label={translation.signUpPageConfirmPasswordLabel}
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                autoComplete="new-password"
                onFocus={() => setErrors(errors => clearServerErrors(errors, 'confirmPassword'))}
                error={!!getServerError(errors, 'confirmPassword')}
                helperText={getServerError(errors, 'confirmPassword')}
                InputProps={{ endAdornment: eyeAdornment(showConfirmPassword, () => setShowConfirmPassword(v => !v)) }}
            />
            <div className='btnHolder fullWidthMobile mt22'>
                <div className="part">
                    <span>
                        {translation.signUpPageHaveAnAccountLabel} &nbsp;
                        <Link component={SigninLink} className='linkPrimary'>
                            {translation.signUpPageLoginLabel}
                        </Link>
                    </span>
                </div>
                <div className="part">
                    <Button
                        disabled={!canSubmit || request.loading}
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                    >
                        {translation.signUpButtonLabel}
                    </Button>
                    {request.loading && <CircularProgress size={24} />}
                </div>
            </div>
        </form>
    )
}

export default SignUpForm
